import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as patientActions from "../../core/actions/patientActions";
import { patientServices, doctorServices } from "../../core/services";
import Breadcrumbs from "../erp-billing/Breadcrumbs";
import PrintPainAssessment from "../patient/print/PrintPainAssessment";
import personDetailsService from "../../core/services/personDetailsService";
import { ConsentFormStatus, FormGroupName } from "../../shared/constants";
import { helperServices } from "../../shared/helper";

const SEVERITY_BANDS = [
  { max: 0, label: "No Pain", color: "#2E7D32" },
  { max: 3, label: "Mild", color: "#66BB6A" },
  { max: 6, label: "Moderate", color: "#FBC02D" },
  { max: 9, label: "Severe", color: "#E65100" },
  { max: 10, label: "Very Severe", color: "#C62828" },
];

const getSeverity = (v) =>
  SEVERITY_BANDS.find((b) => v <= b.max) || SEVERITY_BANDS[SEVERITY_BANDS.length - 1];

const FACE_VALUES = [0, 2, 4, 6, 8, 10];
const FACE_COLORS = ["#0AA653", "#8CCB3E", "#FFF127", "#FF9418", "#F4512C", "#ED1C24"];
const MOUTH_PATHS = [
  "M 10 22 Q 20 35 30 22",
  "M 11 23 Q 20 32 29 23",
  "M 12 26 Q 20 26 28 26",
  "M 11 29 Q 20 19 29 29",
  "M 10 30 Q 20 17 30 30",
];

const PainFace = ({ index, active }) => {
  const color = FACE_COLORS[index];
  const size = 40;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      style={{
        transition: "all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        filter: active ? "drop-shadow(0px 4px 6px rgba(0,0,0,0.25))" : "none",
        cursor: "pointer",
      }}
    >
      <circle cx="20" cy="20" r="18" fill={color} />

      {index === 5 ? (
        <>
          <path d="M 9 15 Q 13 11 17 15" stroke="#111" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <path d="M 23 15 Q 27 11 31 15" stroke="#111" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <path d="M 10 30 Q 20 17 30 30 Q 20 25 10 30 Z" fill="#111" />

          <ellipse cx="7" cy="21" rx="1.4" ry="2.2" fill="#29A9E8" transform="rotate(25 7 21)" />
          <ellipse cx="5" cy="27" rx="1.2" ry="2" fill="#29A9E8" transform="rotate(25 5 27)" />
          <ellipse cx="33" cy="21" rx="1.4" ry="2.2" fill="#29A9E8" transform="rotate(-25 33 21)" />
          <ellipse cx="35" cy="27" rx="1.2" ry="2" fill="#29A9E8" transform="rotate(-25 35 27)" />
        </>
      ) : (
        <>
          <circle cx="14" cy="16" r="2.2" fill="#111" />
          <circle cx="26" cy="16" r="2.2" fill="#111" />
          <path
            d={MOUTH_PATHS[index]}
            stroke="#111"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
        </>
      )}
    </svg>
  );
};

// Robust date string to HTML5 datetime-local string (YYYY-MM-DDTHH:mm) parser
const formatToDateTimeLocal = (dateInput) => {
  if (!dateInput) {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  }

  // Handle direct string formatted values (e.g., "YYYY-MM-DD HH:mm:ss")
  if (typeof dateInput === "string") {
    const cleanStr = dateInput.replace(" ", "T");
    if (cleanStr.length >= 16 && !cleanStr.includes("Z")) {
      return cleanStr.slice(0, 16);
    }
  }

  const d = new Date(dateInput);
  if (isNaN(d.getTime())) {
    const dFallback = new Date();
    dFallback.setMinutes(dFallback.getMinutes() - dFallback.getTimezoneOffset());
    return dFallback.toISOString().slice(0, 16);
  }

  // Adjust for local time representation without UTC conversion shift
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const hasStoredRole = (expectedRole) => {
  const storedRole = localStorage.getItem("role");
  if (!storedRole) return false;

  let parsedRole = storedRole;
  try {
    parsedRole = JSON.parse(storedRole);
  } catch (error) {
    // Use the plain stored value when it is not JSON encoded.
  }

  const roles = Array.isArray(parsedRole) ? parsedRole : [parsedRole];
  return roles.some(
    (role) =>
      String(role).trim().toLowerCase() === expectedRole.toLowerCase(),
  );
};

const PainAssessmentForm = (props) => {
  const dispatch = useDispatch();
  const patientId = props.match.params.patientid;
  const visitId = props.match.params.visitid;
  const groupname = FormGroupName.Pain_Assessment_Form;
  const subgroup = props.match.params.subgroup || "pain_assessment1";
  const isNewMode =
    new URLSearchParams(props.location?.search || "").get("mode") === "new";
  const isDoctorLogin = hasStoredRole("doctor");

  const [header, setHeader] = useState({
    patientName: "",
    age: "",
    gender: "",
    doctorName: "",
    examDateTime: formatToDateTimeLocal(),
    diagnosis: "",
  });

  const [painScore, setPainScore] = useState(0);
  const [doctorGvId, setDoctorGvId] = useState(null);
  const [doctorSigUrl, setDoctorSigUrl] = useState(null);
  const [doctorList, setDoctorList] = useState([]);
  const [doctorSignedAt, setDoctorSignedAt] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [showPrint, setShowPrint] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [formStatus, setFormStatus] = useState(ConsentFormStatus.inprogress);
  

  const existingRowIdsRef = useRef({});

  useEffect(() => {
    // 1. Fetch Patient Details
    dispatch(
      patientActions.getPatientProfile(patientId, visitId, (resCb) => {
        if (!resCb) return;
        setHeader((prev) => ({
          ...prev,
          patientName: `${resCb.fname || ""} ${resCb.mname || ""} ${resCb.lname || ""}`
            .replace(/\s+/g, " ")
            .trim(),
          age: resCb.age || "",
          gender: resCb.gender || "",
        }));
      })
    );

    // 2. Fetch Diagnosis Complaint
    dispatch(
      patientActions.getPatientProcedureInfo(patientId, visitId, "showAllVisits", (resp) => {
        const list = resp?.patientresp?.procedureslist || resp?.procedureslist;
        if (Array.isArray(list)) {
          const complaint = list.find(
            (proc) => proc.claSS === "complaint" && String(proc.visitId) === String(visitId)
          );
          if (complaint?.complaint) {
            setHeader((prev) => ({ ...prev, diagnosis: complaint.complaint }));
          }
        }
      })
    );

    // 3. Fetch Doctor & Examination Date & Time
    dispatch(
      patientActions.getVisitListById(visitId, "showAllVisits", (resCb) => {
        const visitListData = resCb?.patsearchreslt || [];
        const firstVisit = visitListData[0];
        if (!firstVisit) return;

        // Comprehensive extraction of examination date-time across Doctor Section properties
        const rawDoctorExamTime =
          firstVisit.examinationDate ||
          firstVisit.examination_date ||
          firstVisit.visitdate ||
          firstVisit.visitDate ||
          firstVisit.createdat ||
          firstVisit.created_at ||
          firstVisit.assigneddate ||
          (firstVisit.visitAssignmentList && firstVisit.visitAssignmentList[0]?.createdat);

        if (rawDoctorExamTime) {
          setHeader((prev) => ({
            ...prev,
            examDateTime: formatToDateTimeLocal(rawDoctorExamTime),
          }));
        }

        const currentVisitAssignments = firstVisit?.visitAssignmentList || [];
        const personLookup = {};
        if (Array.isArray(resCb?.personList)) {
          resCb.personList.forEach((person) => {
            if (person?.personId != null) personLookup[person.personId] = person;
          });
        }

        const latestDoctorAssignment = [...currentVisitAssignments]
          .reverse()
          .find((a) => a.groupname == null && a.targetid && a.status === "open");

        let doctor = null;
        if (latestDoctorAssignment) doctor = personLookup[latestDoctorAssignment.targetid];
        if (!doctor && latestDoctorAssignment?.targetid) {
          const doctorsDetail = personDetailsService.getDoctorsDetail();
          if (Array.isArray(doctorsDetail)) {
            const foundDoctor = doctorsDetail.find(
              (doc) =>
                doc &&
                (doc.personId == latestDoctorAssignment.targetid ||
                  doc.gvId == latestDoctorAssignment.targetid ||
                  doc.gvid == latestDoctorAssignment.targetid)
            );
            if (foundDoctor) {
              doctor = {
                first_name: foundDoctor.first_name || foundDoctor.firstName || "",
                last_name: foundDoctor.last_name || foundDoctor.lastName || "",
                displaytitle: foundDoctor.displaytitle || foundDoctor.title || "",
                gvId: foundDoctor.gvId || foundDoctor.gvid || foundDoctor.personId,
              };
            }
          }
        }
        if (!doctor && firstVisit.person) doctor = firstVisit.person;

        if (doctor) {
          const doctorName = `${doctor.displaytitle || ""} ${doctor.first_name || ""} ${doctor.last_name || ""}`.trim();
          setHeader((prev) => ({ ...prev, doctorName: doctorName || prev.doctorName }));
          const gvId = doctor.gvId || doctor.gvid || doctor.personId;
          if (gvId != null) setDoctorGvId(gvId);
        }
      })
    );

    doctorServices
      .getDoctorList()
      .then((res) => setDoctorList((res?.personList || []).filter(Boolean)))
      .catch(() => setDoctorList([]));

    
    // Add this near the top of the component, after patientId/visitId/groupname are defined:
    // const isNewMode = new URLSearchParams(props.location?.search || "").get("mode") === "new";

    if (!isNewMode) {
      patientServices
        .getDischargeSummary({}, { patientId, visitId, groupname, subgroup })
        .then((resCb) => {
          const list = resCb?.procedureList;
          if (!Array.isArray(list) || list.length === 0) return;
          setAlreadySaved(true);
          const byClass = {};
          list.forEach((row) => {
            byClass[row.claSS] = row.notes;
            if (row.id != null) existingRowIdsRef.current[row.claSS] = row.id;
          });
          setFormStatus(byClass.status || ConsentFormStatus.inprogress);
          setHeader((prev) => ({
            ...prev,
            patientName: byClass.PatientName || prev.patientName,
            age: byClass.Age || prev.age,
            gender: byClass.Gender || prev.gender,
            doctorName: byClass.DoctorName || prev.doctorName,
            examDateTime: byClass.ExamDateTime ? formatToDateTimeLocal(byClass.ExamDateTime) : prev.examDateTime,
            diagnosis: byClass.Diagnosis || prev.diagnosis,
          }));
          if (byClass.PainScore != null) setPainScore(Number(byClass.PainScore));
          if (byClass.DoctorSignature) setDoctorSigUrl(byClass.DoctorSignature);
          if (byClass.DoctorSignedAt) setDoctorSignedAt(byClass.DoctorSignedAt);
        })
        .catch(() => {});
    } else {
      // Fresh form: clear anything a previous save might have left behind
      setAlreadySaved(false);
      setFormStatus(ConsentFormStatus.inprogress);
      existingRowIdsRef.current = {};
      setPainScore(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


  useEffect(() => {
    if (doctorGvId != null && doctorList.length > 0) {
      const matched = doctorList.find((p) => p && String(p.gvId) === String(doctorGvId));
      if (matched?.signature) setDoctorSigUrl(matched.signature);
    }
  }, [doctorList, doctorGvId]);

  const saveRow = (claSS, notes) =>
    new Promise((resolve, reject) => {
      const existingId = existingRowIdsRef.current[claSS];
      dispatch(
        patientActions.saveProcedureField(
          {
            ...(existingId != null ? { id: existingId } : {}),
            claSS,
            notes: notes != null ? String(notes) : "",
            patientId,
            visitId,
            groupname,
            subgroup,
          },
          (res) => {
            if (
              typeof res === "function" ||
              typeof res === "string" ||
              res?.errorMessages?.length
            ) {
              reject(
                new Error(
                  res?.errorMessages?.[0] ||
                    (typeof res === "string" ? res : "Unable to save the pain assessment."),
                ),
              );
              return;
            }

            const returnedId = res?.procedure?.id;
            if (returnedId != null) existingRowIdsRef.current[claSS] = returnedId;
            resolve(res);
          }
        )
      );
    });

  const handleSave = async () => {
    if (formStatus === ConsentFormStatus.completed) {
      alert("This pain assessment is completed and cannot be edited.");
      return false;
    }

    setSaving(true);
    setSaveStatus("");

    try {
      const now = new Date();
      const signedAt = now.toISOString();

      await saveRow("PatientName", header.patientName);
      await saveRow("Age", header.age);
      await saveRow("Gender", header.gender);
      await saveRow("DoctorName", header.doctorName);
      await saveRow("ExamDateTime", header.examDateTime);
      await saveRow("Diagnosis", header.diagnosis);
      await saveRow("PainScore", painScore);
      await saveRow("status", ConsentFormStatus.inprogress);
      if (doctorSigUrl) {
        await saveRow("DoctorSignature", doctorSigUrl);
      }
      await saveRow("DoctorSignedAt", signedAt);

      setDoctorSignedAt(signedAt);
      setSaveStatus("saved");
      setAlreadySaved(true);
      return true;
    } catch (e) {
      console.error("Pain assessment save failed:", e);
      setSaveStatus("error");
      return false;
    } finally {
      setSaving(false);
    }
  }; 

  const handlePrint = async () => {
    if (saving) return;

    if (!alreadySaved) {
      const saved = await handleSave();
      if (!saved) return;
    }

    setShowPrint(true);
  };

  const crumbs = [
  { label: `${(header.patientName || "").toUpperCase()} ${header.age} ${header.gender}`.trim(),
    path: `/PatientSections/${patientId}/${visitId}`,
  },
  { label: "Consent Forms",
    path: `/PatientSections/${patientId}/${visitId}/consent-forms`
  },
  { label: "Pain Assessment" }, 
  ];

  const cardStyle = {
    background: "#ffffff",
    border: "1px solid #dcdfe6",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    padding: 20,
    marginBottom: 16,
  };

  const sectionHeaderStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#2d3748",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #e2e8f0",
    paddingBottom: 8,
    marginBottom: 16,
  };

  const activeFaceIndex = Math.min(5, Math.round(painScore / 2));
  const severity = getSeverity(painScore);
  const isCompleted = formStatus === ConsentFormStatus.completed;

  const signedDateTime = doctorSignedAt
    ? helperServices.formatDateNTime(doctorSignedAt, true)
    : "";
  const [signedDate = "", signedTime = "", signedMeridiem = ""] =
    signedDateTime.split(" ");

  const handleClose = () => {
    if (props.onClose) {
      props.onClose();
      return;
    }

    props.history?.push("/patient");
  };

  return (
    <div 
      className="mainsection" 
      style={{ 
        paddingBottom: 80, 
        backgroundColor: "#ffffff", 
        minHeight: "100vh",
        paddingLeft: 16,
        paddingRight: 16
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <Breadcrumbs crumbs={crumbs} />
        </div>
        {isDoctorLogin && (
          <button
            type="button"
            aria-label="Close Pain Assessment"
            title="Close"
            onClick={handleClose}
            style={{
              border: "none",
              background: "transparent",
              color: "#9ca3af",
              fontSize: 28,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              padding: 8,
            }}
          >
            &times;
          </button>
        )}
      </div>

      {isCompleted && (
        <div className="alert alert-info py-2" role="status">
          This pain assessment is completed and is available in read-only mode.
        </div>
      )}

      <fieldset
        disabled={isCompleted}
        style={{
          border: 0,
          margin: 0,
          minWidth: 0,
          padding: 0,
          pointerEvents: isCompleted ? "none" : "auto",
        }}
      >
      <div 
        style={{ 
          display: "flex", 
          flexDirection: "row", 
          gap: "20px", 
          alignItems: "flex-start", 
          marginTop: "16px",
          width: "100%",
          backgroundColor: "#ffffff"
        }}
      >
        {/* LEFT COLUMN: Input Form Controls */}
        <div style={{ flex: "1 1 50%", width: "50%", minWidth: "0" }}>
          <div style={cardStyle}>
            <div style={sectionHeaderStyle}>Patient & Examination Details</div>
            
            <div className="form-group mb-3">
              <label style={{ fontSize: 12, fontWeight: 600, color: "#4a5568" }}>Patient Name </label>
              <input 
                className="form-control" 
                value={header.patientName}  
                readOnly 
                disabled 
                style={{ background: "#f7fafc", fontWeight: 600, color: "#2d3748" }} 
              />
            </div>

            <div className="form-group mb-3">
              <label style={{ fontSize: 12, fontWeight: 600, color: "#4a5568" }}>Date and Time of Examination</label>
              <input
                type="datetime-local"
                className="form-control"
                value={header.examDateTime}
                onChange={(e) => setHeader((p) => ({ ...p, examDateTime: e.target.value }))}
              />
            </div>

            <div className="form-group mb-3">
              <label style={{ fontSize: 12, fontWeight: 600, color: "#4a5568" }}>Age / Sex</label>
              <input 
                className="form-control" 
                value={`${header.age} / ${header.gender}`} 
                readOnly 
                disabled 
                style={{ background: "#f7fafc" }} 
              />
            </div>

            <div className="form-group mb-3">
              <label style={{ fontSize: 12, fontWeight: 600, color: "#4a5568" }}>Name of Doctor</label>
              <input 
                className="form-control" 
                value={header.doctorName} 
                readOnly 
                disabled 
                style={{ background: "#f7fafc" }} 
              />
            </div>

            <div className="form-group mb-1">
              <label style={{ fontSize: 12, fontWeight: 600, color: "#4a5568" }}>Diagnosis </label>
              <textarea
                className="form-control"
                rows={6}
                value={header.diagnosis}
                onChange={(e) => setHeader((p) => ({ ...p, diagnosis: e.target.value }))}
                placeholder="Enter clinical diagnosis or complaint..."
                style={{ resize: "vertical" }}
              />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Pain Rating Scale & Sign-off */}
        <div style={{ flex: "1 1 50%", width: "50%", minWidth: "0" }}>
          <div style={cardStyle}>
            <div style={sectionHeaderStyle}>Pain Rating Scale (Visual Analog Scale)</div>

            <div 
              className="d-flex justify-content-between align-items-center mb-4 px-2" 
              style={{ background: "#fafafa", padding: "16px 8px", borderRadius: 8, border: "1px solid #e2e8f0" }}
            >
              {FACE_VALUES.map((v, i) => (
                <div
                  key={v}
                  className="text-center"
                  onClick={() => setPainScore(v)}
                  style={{ cursor: "pointer", width: 48 }}
                >
                  <PainFace index={i} active={activeFaceIndex === i} />
                  <div
                    className="mt-2"
                    style={{
                      fontSize: 12,
                      color: activeFaceIndex === i ? FACE_COLORS[i] : "#718096",
                      fontWeight: activeFaceIndex === i ? 700 : 500,
                    }}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-2 mb-2">
              <input
                type="range"
                min="0"
                max="10"
                step="1"
                value={painScore}
                onChange={(e) => setPainScore(Number(e.target.value))}
                style={{
                  width: "100%",
                  height: 8,
                  borderRadius: 4,
                  outline: "none",
                  accentColor: severity.color,
                  cursor: "pointer",
                }}
              />
            </div>

            <div className="d-flex justify-content-between px-1 mb-4" style={{ fontSize: 11 }}>
              {Array.from({ length: 11 }, (_, i) => (
                <span
                  key={i}
                  onClick={() => setPainScore(i)}
                  style={{
                    color: i === painScore ? getSeverity(i).color : "#a0aec0",
                    fontWeight: i === painScore ? 700 : 500,
                    cursor: "pointer",
                    width: 18,
                    textAlign: "center",
                  }}
                >
                  {i}
                </span>
              ))}
            </div>

            <div
              className="text-center py-2 px-3"
              style={{
                background: severity.color,
                color: "#ffffff",
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: "0.5px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                transition: "background 0.2s ease",
              }}
            >
              Score: {painScore} — {severity.label.toUpperCase()}
            </div>
          </div>

          <div style={cardStyle}>
            <div style={sectionHeaderStyle}>Doctor Sign-off</div>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <div style={{ flex: 2 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#4a5568" }}>Signature of Doctor</label>
                <div
                  style={{
                    border: "1px dashed #cbd5e0",
                    borderRadius: 6,
                    padding: 6,
                    textAlign: "center",
                    minHeight: 65,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#fafafa",
                  }}
                >
                  {doctorSigUrl ? (
                    <img src={doctorSigUrl} alt="Doctor Signature" style={{ maxHeight: 50, maxWidth: "100%" }} />
                  ) : (
                    <span className="text-muted" style={{ fontSize: 11 }}>
                      No digital signature on file
                    </span>
                  )}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#4a5568" }}>Date</label>
                <input 
                  className="form-control form-control-sm" 
                  value={signedDate}
                  readOnly 
                  disabled 
                  style={{ background: "#f7fafc" }} 
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#4a5568" }}>Time</label>
                <input 
                  className="form-control form-control-sm" 
                  value={[signedTime, signedMeridiem].filter(Boolean).join(" ")}
                  readOnly 
                  disabled 
                  style={{ background: "#f7fafc" }} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      </fieldset>

      {/* Floating Action Bar */}
      <div
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: "#ffffff",
          padding: "10px 18px",
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          zIndex: 200,
          border: "1px solid #e2e8f0",
        }}
      >
        {saveStatus === "saved" && <span className="text-success fw-bold" style={{ fontSize: 13 }}>✓ Saved</span>}
        {saveStatus === "error" && <span className="text-danger fw-bold" style={{ fontSize: 13 }}>✕ Save failed</span>}
        
        <button className="btn btn-outline-primary btn-sm" onClick={handlePrint} disabled={saving}>
          Print
        </button>
        {!isCompleted && (
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : alreadySaved ? "Update" : "Save"}
          </button>
        )}
      </div>

      {showPrint && (
        <PrintPainAssessment
          patientInfo={{
            fullName: header.patientName,
            age: header.age,
            gender: header.gender,
            doctorName: header.doctorName,
            diagnosis: header.diagnosis,
            painScore,
            doctorSignature: doctorSigUrl,
            doctorSignedAt,
            patientId,
            visitId,
          }}
          regDate={header.examDateTime}
          parentCallBack={() => setShowPrint(false)}
        />
      )}  
    </div>
  );
};

export default PainAssessmentForm;
